import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import processTokenResponse from '@/modules/auth/functions/processTokenResponse';
import useTokenStore from '@/modules/auth/stores/useTokenStore';
import config from '@/modules/config/functions/config';
import {
  AuthRefreshDataProcessingError,
  AuthRefreshFetchError,
  AuthRefreshGeneralError,
  AuthRefreshInternalServerError,
  AuthRefreshRequestError,
  AuthRefreshServerConnectionError,
  AuthRefreshUnauthorizedError,
  AuthStartupExpiredRefreshTokenError,
  AuthStartupInvalidRefreshTokenError,
  AuthStartupNoRefreshTokenError,
  BaseError,
  CoreAuthUnauthorizedError,
} from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import attachCommonWretchErrorHandlers from '@/modules/query-client/functions/attachCommonWretchErrorHandlers';
import createCommonWretchErrorHandlerMap from '@/modules/query-client/functions/createCommonWretchErrorHandlerMap';
import handleWretchFetchError from '@/modules/query-client/functions/handleWretchFetchError';
import usePublicWretch from '@/modules/query-client/hooks/usePublicWretch';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { isNil } from 'lodash-es';

export default function useRefreshTokenMutation({
  shouldStoreTokens = true,
  ...options
}: UseMutationOptions<{
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken?: string | undefined;
  refreshTokenExpiresAt?: number | undefined;
}> & {
  shouldStoreTokens?: boolean;
} = {}) {
  const { getRefreshToken, setAccessToken, setRefreshToken } = useTokenStore();
  const httpClient = usePublicWretch();

  return useMutation({
    mutationKey: ['auth', 'refresh-token'],
    mutationFn: async () => {
      const refreshToken = getRefreshToken();

      if (refreshToken === undefined)
        throw new AuthStartupNoRefreshTokenError();

      if (refreshToken === 'expired')
        throw new AuthStartupExpiredRefreshTokenError();

      if (refreshToken === 'invalid')
        throw new AuthStartupInvalidRefreshTokenError();

      return attachCommonWretchErrorHandlers(
        httpClient
          .url('api/v1/access-token/refresh')
          .json({
            refreshToken: refreshToken.token,
          })
          .post(),
        createCommonWretchErrorHandlerMap([
          ['badRequest', AuthRefreshRequestError],
          ['unauthorized', AuthRefreshUnauthorizedError],
          ['timeout', AuthRefreshServerConnectionError],
          ['internalError', AuthRefreshInternalServerError],
          [502, AuthRefreshServerConnectionError],
          [503, AuthRefreshServerConnectionError],
          [504, AuthRefreshServerConnectionError],
          ['fetchError', AuthRefreshFetchError],
        ])
      )
        .json<{
          accessToken: string;
          accessTokenExpiresIn: number;
          refreshToken?: string;
          refreshTokenExpiresIn: number;
        }>()
        .then((data) => {
          const tokens = processTokenResponse({
            response: data,
            InvalidAccessTokenErrorClass: AuthRefreshDataProcessingError,
            InvalidRefreshTokenErrorClass: AuthRefreshDataProcessingError,
          });

          if (shouldStoreTokens !== true) return tokens;

          setAccessToken(tokens.accessToken, tokens.accessTokenExpiresAt);
          if (
            config('features.auth.enableTokenRefreshing') === true &&
            !isNil(tokens.refreshToken) &&
            !isNil(tokens.refreshTokenExpiresAt)
          )
            setRefreshToken(tokens.refreshToken, tokens.refreshTokenExpiresAt);
          return tokens;
        })
        .catch((error) => {
          handleWretchFetchError(error, 'auth.refresh', AuthRefreshFetchError);
          if (error instanceof BaseError) {
            logger.error('auth.refresh', error);
            throw error;
          } else {
            logger.error('auth.refresh', error);
            throw new AuthRefreshGeneralError(makeErrorFromUnknown(error));
          }
        });
    },
    retry(failureCount, error) {
      if (
        error instanceof AuthStartupNoRefreshTokenError ||
        error instanceof AuthStartupInvalidRefreshTokenError ||
        error instanceof AuthStartupExpiredRefreshTokenError ||
        error instanceof CoreAuthUnauthorizedError ||
        error instanceof AuthRefreshUnauthorizedError
      )
        return false;
      return failureCount < config('misc.defaultMutationRetryCount', 3);
    },
    ...options,
  });
}
